import React, { useEffect } from 'react';
import _ from 'lodash';
import styled from 'styled-components';
import { useRouter } from 'next/compat/router';
import dynamic from 'next/dynamic';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useMenuFilterOptions from 'hooks/use-menu-filter-options';
import { useDispensaryCategory } from 'src/hooks/use-dispensary-category';
import { useCollectionFallbackForProductsRoute } from 'src/dispensary/hooks/use-collection-fallback-for-products-route';
import { useMenuHeaderHeading } from 'components/menu-header/use-menu-header-heading';
import { tracker } from 'utils/analytics';
import { searchResultsViewed } from 'utils/ecommerce-analytics';

import { MobileOnly } from 'shared/components';
import Menu from 'components/menu';
import SubcategoriesPills from 'components/subcategories-pills';
import { CategorySubcategoryPills } from 'components/category-subcategory-pills';
import { AmplitudeEvents, useAmplitude } from 'shared/hooks/use-amplitude';
import useStores from 'shared/hooks/use-stores';
import { useProductsForCategoryPage } from 'src/dispensary/hooks/use-products-for-category-page';
import { Typography } from 'src/typography';
import { useRemoteComponent, RemoteComponentSlot } from 'hooks/use-remote-component';

// Loaded on demand, only when the :category param resolves to a custom category
// collection, to avoid pulling the (heavier) collections page code into the
// standard products/category bundle.
const DispensaryCollectionsPage = dynamic(() =>
  import('src/dispensary/collections').then((mod) => mod.DispensaryCollectionsPage)
);

export function CoreMenuCategoryPage() {
  const { UI } = useStores();
  const { dispensary } = useDispensary();
  const category = useDispensaryCategory();
  const collectionFallback = useCollectionFallbackForProductsRoute(category);
  const heading = useMenuHeaderHeading({ category, page: 'category' });

  const ListPageHero = useRemoteComponent(RemoteComponentSlot.ListPageHero);

  const { products, loading, totalCount, totalPages, productsCarouselQueries } = useProductsForCategoryPage({
    perPage: UI.isKiosk ? 20 : 100,
  });

  const { categoriesSubcategories } = useMenuFilterOptions();
  const amplitude = useAmplitude();
  const router = useRouter();
  const categoryName = router?.query?.category;
  const searchQuery = router?.query?.search;
  const inAllCategoriesView = UI.onSearchPage;

  useEffect(() => {
    if (!_.isEmpty(categoryName) && !_.isEmpty(dispensary?.id)) {
      amplitude.log(AmplitudeEvents.menu.viewCategory, {
        categoryName,
        dispensaryId: dispensary.id,
        dispensaryName: dispensary?.name,
        menuVariant: UI.analyticsMenuVariant,
      });
    }
    // rationale: exhaustive deps cause these events to be fired more than we want
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryName]);

  useEffect(() => {
    if (!loading && searchQuery && dispensary && products) {
      tracker.setContext({
        activeDispensary: dispensary,
      });

      tracker.searchResults({ query: searchQuery, hasResults: products.length > 0, products });
      searchResultsViewed(searchQuery, products);
    }
    // rationale: exhaustive deps cause these events to be fired more than we want
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, searchQuery]);

  if (!dispensary) {
    return null;
  }

  // The :category param didn't match a real category, but it does match a collection.
  // Render the collection so /products/:slug works the same as /collections/:slug,
  // whether or not the collection is configured to appear as a custom category.
  if (collectionFallback) {
    return <DispensaryCollectionsPage slugOverride={collectionFallback.slug} />;
  }

  return (
    <>
      <div data-testid='list-page-hero'>
        <ListPageHero />
      </div>
      <MobileOnly>
        <MobileHeadingContainer>
          <CategoryHeadingContainer>
            <Typography.Heading size='medium' tag='h1' casing='capitalize' data-testid='menu-header-title'>
              {heading}
            </Typography.Heading>
          </CategoryHeadingContainer>

          {inAllCategoriesView ? (
            <CategorySubcategoryPills categoriesSubcategories={categoriesSubcategories} />
          ) : (
            <SubcategoriesPillsContainer />
          )}
        </MobileHeadingContainer>
      </MobileOnly>
      <Menu
        products={products}
        totalCount={totalCount}
        totalPages={totalPages}
        loading={loading}
        dispensary={dispensary}
        page='category'
        productsCarouselQueries={productsCarouselQueries}
      />
    </>
  );
}

const SubcategoriesPillsContainer = styled(SubcategoriesPills)`
  margin-top: 16px;
`;

const CategoryHeadingContainer = styled.div`
  padding: 16px 0 0 16px;
`;

const MobileHeadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow: scroll;
  margin-bottom: -9px;
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }
`;
